'use client';

import { t } from 'mobx-state-tree';

import type { Instance } from 'mobx-state-tree';

/** Flip to true locally when debugging overlay queue / pinned slot behaviour. */
const DEBUG_LOG = false;

/** Minimum time an overlay id stays visible after hide before removal. */
export const MIN_LOADING_OVERLAY_DISPLAY_MS = 1500;
const OVERLAY_REMOVAL_DELAY_MS = 0;
const UNPINNED_CLOSE_FALLBACK_REMOVAL_MS = MIN_LOADING_OVERLAY_DISPLAY_MS + 1000;

/** One line in an unpinned overlay id queue (stable key for enter/exit animations). */
const LoadingOverlayQueuedLineModel = t.model('LoadingOverlayQueuedLine', {
  key: t.string,
  text: t.string,
});

let unpinnedOverlayQueueKeySeq = 0;

function makeUnpinnedQueueLineKey(id: string): string {
  unpinnedOverlayQueueKeySeq += 1;
  return `uq-${id}-${Date.now()}-${unpinnedOverlayQueueKeySeq}`;
}

/** Options for `globalStore.showLoadingOverlay`. */
export type ShowLoadingOverlayOptions = {
  message?: string;
  /** When true, registers in the pinned headline slot; otherwise unpinned stack. */
  pinned?: boolean;
  /** When set, updates stored preference for this id (dimmed cinematic backdrop while visible). */
  useDimmedBackdrop?: boolean;
  /** When true, keep a single unpinned queue entry (no backlog) and update its text in place. */
  replace?: boolean;
};

/** Options for `globalStore.hideLoadingOverlay`. */
export type HideLoadingOverlayOptions = {
  /** When true, remove the unpinned slot immediately instead of animating close. */
  immediate?: boolean;
};

type LoadingOverlaySlot = 'pinned' | 'unpinned';

/** Track timestamps and timeout IDs for minimum display duration (per id + slot). */
const loadingOverlayTimestamps = new Map<string, number>();
const loadingOverlayTimeouts = new Map<string, NodeJS.Timeout>();

function overlayTimestampKey(id: string, slot: LoadingOverlaySlot): string {
  return `${id}:${slot}`;
}

export const LoadingOverlayStoreModel = t
  .model('LoadingOverlayStore', {
    /** Pinned slot per overlay id (headline row). */
    loadingOverlayPinnedMessages: t.map(t.string),
    /** Unpinned slot per overlay id (secondary stack). */
    loadingOverlayUnpinnedMessages: t.map(t.string),
    /** Insertion order for pinned slots (first id supplies the headline). */
    pinnedOverlayIds: t.array(t.string),
    /** Insertion order for unpinned stack. */
    unpinnedOverlayIds: t.array(t.string),
    /** Per overlay id: use dimmed backdrop layers while this id is active. */
    loadingOverlayUseDimmedBackdropById: t.optional(t.map(t.boolean), {}),
    /** FIFO message queue per unpinned overlay id (head is the active line). */
    loadingOverlayUnpinnedMessageQueues: t.optional(t.map(t.array(LoadingOverlayQueuedLineModel)), {}),
    /** When true, the current head line should exit and the slot removes (pending tail was dropped on hide). */
    loadingOverlayUnpinnedCloseAfterCurrent: t.optional(t.map(t.boolean), {}),
  })
  .views((self) => ({
    get loadingOverlay() {
      return self.pinnedOverlayIds.length > 0 || self.unpinnedOverlayIds.length > 0;
    },
    get loadingOverlayMessage() {
      if (self.unpinnedOverlayIds.length === 0) return '';
      const topId = self.unpinnedOverlayIds[self.unpinnedOverlayIds.length - 1];
      const q = self.loadingOverlayUnpinnedMessageQueues.get(topId);
      return q?.[0]?.text ?? self.loadingOverlayUnpinnedMessages.get(topId) ?? '';
    },
    get pinnedLoadingOverlayMessage() {
      if (self.pinnedOverlayIds.length === 0) return null;
      const firstId = self.pinnedOverlayIds[0];
      return self.loadingOverlayPinnedMessages.get(firstId) ?? null;
    },
    /**
     * Lines for the loading overlay: optional pinned headline plus secondary stack in id order.
     * When there is no pin, `primaryOnly` is the top-of-stack unpinned message (single-line UX).
     * Duplicate visible copy is suppressed (one line per normalised message across pinned and unpinned heads).
     *
     * `secondaryFeed`, `pinnedKey`, and `primaryLineKey` use overlay registration ids (not message text)
     * so list items can use stable React keys for enter/exit animations.
     */
    get loadingOverlayDisplayMessages() {
      let pinnedLine: string | null = null;
      let pinnedKey: string | null = null;
      if (self.pinnedOverlayIds.length > 0) {
        for (const id of self.pinnedOverlayIds) {
          const raw = self.loadingOverlayPinnedMessages.get(id);
          const trimmed = raw?.trim() ?? '';
          if (trimmed.length > 0) {
            pinnedLine = trimmed;
            pinnedKey = id;
            break;
          }
        }
      }
      const seen = new Set<string>();
      if (pinnedLine) {
        seen.add(pinnedLine);
      }
      const secondaryLines: string[] = [];
      const secondaryFeed: { id: string; text: string }[] = [];
      let previousLine = '';
      for (const id of self.unpinnedOverlayIds) {
        const q = self.loadingOverlayUnpinnedMessageQueues.get(id);
        const line = (q?.[0]?.text ?? self.loadingOverlayUnpinnedMessages.get(id) ?? '').trim();
        if (line.length === 0) {
          continue;
        }
        if (seen.has(line)) {
          continue;
        }
        if (line === previousLine) {
          continue;
        }
        secondaryLines.push(line);
        secondaryFeed.push({ id, text: line });
        seen.add(line);
        previousLine = line;
      }
      let primaryOnly = '';
      let primaryLineKey: string | null = null;
      if (!pinnedLine && self.unpinnedOverlayIds.length > 0) {
        const topId = self.unpinnedOverlayIds[self.unpinnedOverlayIds.length - 1];
        primaryOnly = self.loadingOverlayUnpinnedMessages.get(topId)?.trim() ?? '';
        primaryLineKey = topId;
      }
      return { pinnedLine, secondaryLines, primaryOnly, secondaryFeed, pinnedKey, primaryLineKey };
    },
    /** True if any active overlay id prefers the dimmed backdrop. */
    get loadingOverlayUseDimmedBackdrop() {
      const seen = new Set<string>();
      for (const id of self.pinnedOverlayIds) {
        seen.add(id);
      }
      for (const id of self.unpinnedOverlayIds) {
        seen.add(id);
      }
      for (const id of seen) {
        if (self.loadingOverlayUseDimmedBackdropById.get(id) === true) {
          return true;
        }
      }
      return false;
    },
  }))
  .actions((self) => ({
    showLoadingOverlay(id: string, options?: ShowLoadingOverlayOptions) {
      applyShowLoadingOverlay(self as ILoadingOverlayStore, id, options);
    },
    hideLoadingOverlay(id: string, pinned?: boolean, options?: HideLoadingOverlayOptions) {
      applyHideLoadingOverlay(self as ILoadingOverlayStore, id, pinned, options);
    },
    completeUnpinnedOverlayHead(id: string) {
      completeUnpinnedOverlayHeadAction(self as ILoadingOverlayStore, id);
    },
    _removeLoadingOverlaySlot(id: string, slot: LoadingOverlaySlot) {
      const key = overlayTimestampKey(id, slot);
      const pending = loadingOverlayTimeouts.get(key);
      if (pending) {
        clearTimeout(pending);
        loadingOverlayTimeouts.delete(key);
      }
      loadingOverlayTimestamps.delete(key);

      if (slot === 'pinned') {
        self.loadingOverlayPinnedMessages.delete(id);
        const idx = self.pinnedOverlayIds.indexOf(id);
        if (idx !== -1) {
          self.pinnedOverlayIds.splice(idx, 1);
        }
      } else {
        self.loadingOverlayUnpinnedMessages.delete(id);
        const idx = self.unpinnedOverlayIds.indexOf(id);
        if (idx !== -1) {
          self.unpinnedOverlayIds.splice(idx, 1);
        }
        self.loadingOverlayUnpinnedMessageQueues.delete(id);
        self.loadingOverlayUnpinnedCloseAfterCurrent.delete(id);
      }
      if (!self.pinnedOverlayIds.includes(id) && !self.unpinnedOverlayIds.includes(id)) {
        self.loadingOverlayUseDimmedBackdropById.delete(id);
      }
    },
  }));

export type ILoadingOverlayStore = Instance<typeof LoadingOverlayStoreModel>;

function normaliseOverlayMessageForCompare(text: string): string {
  return text
    .replace(/\u2026/g, '...')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** True if any currently visible overlay line (pinned or unpinned head) uses this normalised text. */
function anyVisibleLineHasNormalised(self: ILoadingOverlayStore, normalised: string): boolean {
  if (normalised.length === 0) {
    return false;
  }
  for (const pid of self.pinnedOverlayIds) {
    const raw = self.loadingOverlayPinnedMessages.get(pid);
    if (!raw?.trim()) {
      continue;
    }
    if (normaliseOverlayMessageForCompare(raw) === normalised) {
      return true;
    }
  }
  for (const uid of self.unpinnedOverlayIds) {
    const q = self.loadingOverlayUnpinnedMessageQueues.get(uid);
    const headRaw = (q?.[0]?.text ?? self.loadingOverlayUnpinnedMessages.get(uid) ?? '').trim();
    if (!headRaw) {
      continue;
    }
    if (normaliseOverlayMessageForCompare(headRaw) === normalised) {
      return true;
    }
  }
  return false;
}

/**
 * Skip showing this message if the same copy is already visible on another line.
 * Pinned vs unpinned does not matter - only one visible line per distinct message.
 */
function shouldSkipDuplicateOverlayMessage(
  self: ILoadingOverlayStore,
  id: string,
  incomingPinned: boolean,
  normalised: string,
): boolean {
  if (normalised.length === 0) {
    return false;
  }
  if (!anyVisibleLineHasNormalised(self, normalised)) {
    return false;
  }
  if (incomingPinned) {
    const mine = normaliseOverlayMessageForCompare(self.loadingOverlayPinnedMessages.get(id) ?? '');
    if (mine === normalised) {
      return false;
    }
  } else {
    const q = self.loadingOverlayUnpinnedMessageQueues.get(id);
    const headRaw = q?.[0]?.text ?? self.loadingOverlayUnpinnedMessages.get(id) ?? '';
    const mine = normaliseOverlayMessageForCompare(headRaw);
    if (mine === normalised) {
      return false;
    }
  }
  return true;
}

function syncUnpinnedHeadFromQueue(self: ILoadingOverlayStore, id: string) {
  const q = self.loadingOverlayUnpinnedMessageQueues.get(id);
  const head = q?.[0];
  if (head) {
    self.loadingOverlayUnpinnedMessages.set(id, head.text);
  } else {
    self.loadingOverlayUnpinnedMessages.delete(id);
  }
}

/**
 * Ensure at most one visible unpinned head shares a given normalised message with pinned or an earlier unpinned id.
 * Walk order matches `unpinnedOverlayIds` (first registered id wins duplicate text).
 */
function dedupeVisibleOverlayHeads(self: ILoadingOverlayStore) {
  const claimed = new Set<string>();
  for (const pid of self.pinnedOverlayIds) {
    const raw = self.loadingOverlayPinnedMessages.get(pid);
    if (!raw?.trim()) {
      continue;
    }
    claimed.add(normaliseOverlayMessageForCompare(raw));
  }

  for (const uid of [...self.unpinnedOverlayIds]) {
    const q = self.loadingOverlayUnpinnedMessageQueues.get(uid);
    if (!q || q.length === 0) {
      continue;
    }

    let safety = 0;
    while (q.length > 0 && safety < 64) {
      safety += 1;
      const headRaw = q[0].text.trim();
      if (!headRaw) {
        q.splice(0, 1);
        continue;
      }
      const n = normaliseOverlayMessageForCompare(headRaw);
      if (claimed.has(n)) {
        q.splice(0, 1);
        self.loadingOverlayUnpinnedCloseAfterCurrent.delete(uid);
        continue;
      }
      claimed.add(n);
      break;
    }

    if (q.length === 0) {
      self.loadingOverlayUnpinnedMessageQueues.delete(uid);
      self._removeLoadingOverlaySlot(uid, 'unpinned');
    } else {
      syncUnpinnedHeadFromQueue(self, uid);
    }
  }
}

/** True if any unpinned id's visible head matches `normalised` (queue head or legacy map). */
function unpinnedHeadHasMessage(self: ILoadingOverlayStore, normalised: string): boolean {
  for (const uid of self.unpinnedOverlayIds) {
    const q = self.loadingOverlayUnpinnedMessageQueues.get(uid);
    const headRaw = (q?.[0]?.text ?? self.loadingOverlayUnpinnedMessages.get(uid) ?? '').trim();
    if (normaliseOverlayMessageForCompare(headRaw) === normalised) {
      return true;
    }
  }
  return false;
}

function enqueueUnpinnedOverlayMessage(self: ILoadingOverlayStore, id: string, trimmed: string) {
  const line = LoadingOverlayQueuedLineModel.create({
    key: makeUnpinnedQueueLineKey(id),
    text: trimmed,
  });
  const existing = self.loadingOverlayUnpinnedMessageQueues.get(id);
  if (existing) {
    existing.push(line);
  } else {
    self.loadingOverlayUnpinnedMessageQueues.set(id, [line]);
  }
  syncUnpinnedHeadFromQueue(self, id);
  self.loadingOverlayUnpinnedCloseAfterCurrent.delete(id);
}

function replaceUnpinnedOverlayMessage(self: ILoadingOverlayStore, id: string, trimmed: string) {
  const normalised = normaliseOverlayMessageForCompare(trimmed);
  const existing = self.loadingOverlayUnpinnedMessageQueues.get(id);
  if (existing?.length === 1) {
    const head = existing[0];
    if (normaliseOverlayMessageForCompare(head.text) === normalised) {
      syncUnpinnedHeadFromQueue(self, id);
      self.loadingOverlayUnpinnedCloseAfterCurrent.delete(id);
      return;
    }
    head.text = trimmed;
    syncUnpinnedHeadFromQueue(self, id);
    self.loadingOverlayUnpinnedCloseAfterCurrent.delete(id);
    return;
  }
  const line = LoadingOverlayQueuedLineModel.create({
    key: makeUnpinnedQueueLineKey(id),
    text: trimmed,
  });
  self.loadingOverlayUnpinnedMessageQueues.set(id, [line]);
  syncUnpinnedHeadFromQueue(self, id);
  self.loadingOverlayUnpinnedCloseAfterCurrent.delete(id);
}

function requestUnpinnedOverlayClose(self: ILoadingOverlayStore, id: string) {
  const key = overlayTimestampKey(id, 'unpinned');
  const existingTimeout = loadingOverlayTimeouts.get(key);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    loadingOverlayTimeouts.delete(key);
  }

  const q = self.loadingOverlayUnpinnedMessageQueues.get(id);
  if (!q || q.length === 0) {
    if (self.unpinnedOverlayIds.includes(id)) {
      self._removeLoadingOverlaySlot(id, 'unpinned');
    }
    return;
  }
  while (q.length > 1) {
    q.pop();
  }
  self.loadingOverlayUnpinnedCloseAfterCurrent.set(id, true);
  syncUnpinnedHeadFromQueue(self, id);

  // Failsafe: if the UI exit callback is missed (e.g. during reload/remount), do not leave stale ids behind.
  const fallbackTimeout = setTimeout(() => {
    if (self.unpinnedOverlayIds.includes(id)) {
      self._removeLoadingOverlaySlot(id, 'unpinned');
    }
  }, UNPINNED_CLOSE_FALLBACK_REMOVAL_MS);
  loadingOverlayTimeouts.set(key, fallbackTimeout);
}

function completeUnpinnedOverlayHeadAction(self: ILoadingOverlayStore, id: string) {
  const q = self.loadingOverlayUnpinnedMessageQueues.get(id);
  if (!q || q.length === 0) {
    return;
  }
  q.splice(0, 1);
  self.loadingOverlayUnpinnedCloseAfterCurrent.delete(id);
  if (q.length === 0) {
    self.loadingOverlayUnpinnedMessageQueues.delete(id);
    self._removeLoadingOverlaySlot(id, 'unpinned');
  } else {
    syncUnpinnedHeadFromQueue(self, id);
  }
  dedupeVisibleOverlayHeads(self);
}

function pinnedHasMessage(self: ILoadingOverlayStore, normalised: string): boolean {
  for (const v of self.loadingOverlayPinnedMessages.values()) {
    if (normaliseOverlayMessageForCompare(v) === normalised) {
      return true;
    }
  }
  return false;
}

/** Remove unpinned queue entries whose message matches a pinned line (pinned headline wins that text). */
function removeUnpinnedSlotsWithMessage(self: ILoadingOverlayStore, normalised: string) {
  const ids = [...self.unpinnedOverlayIds];
  for (const uid of ids) {
    const q = self.loadingOverlayUnpinnedMessageQueues.get(uid);
    if (!q || q.length === 0) {
      const legacy = normaliseOverlayMessageForCompare(self.loadingOverlayUnpinnedMessages.get(uid) ?? '');
      if (legacy === normalised) {
        self._removeLoadingOverlaySlot(uid, 'unpinned');
      }
      continue;
    }
    for (let i = q.length - 1; i >= 0; i -= 1) {
      if (normaliseOverlayMessageForCompare(q[i].text) === normalised) {
        q.splice(i, 1);
      }
    }
    if (q.length === 0) {
      self._removeLoadingOverlaySlot(uid, 'unpinned');
      continue;
    }
    syncUnpinnedHeadFromQueue(self, uid);
  }
}

function maybeSetDimmedBackdropPreference(
  self: ILoadingOverlayStore,
  id: string,
  useDimmedBackdrop: boolean | undefined,
) {
  if (useDimmedBackdrop === undefined) {
    return;
  }
  if (self.pinnedOverlayIds.includes(id) || self.unpinnedOverlayIds.includes(id)) {
    self.loadingOverlayUseDimmedBackdropById.set(id, useDimmedBackdrop);
  }
}

function applyShowLoadingOverlay(self: ILoadingOverlayStore, id: string, options?: ShowLoadingOverlayOptions) {
  const message = options?.message;
  const pinned = options?.pinned;
  const useDimmedBackdrop = options?.useDimmedBackdrop;
  const isPinnedSlot = pinned === true;
  const isUnpinnedSlot = pinned !== true;

  if (DEBUG_LOG) {
    console.log('[Overlay] showLoadingOverlay', id, message, pinned === true ? 'PINNED' : '', useDimmedBackdrop);
  }

  const trimmed = message?.trim() ?? '';
  const normalised = normaliseOverlayMessageForCompare(trimmed);

  if (normalised.length > 0) {
    if (shouldSkipDuplicateOverlayMessage(self, id, isPinnedSlot, normalised)) {
      maybeSetDimmedBackdropPreference(self, id, useDimmedBackdrop);
      return;
    }
    if (isPinnedSlot) {
      removeUnpinnedSlotsWithMessage(self, normalised);
      if (pinnedHasMessage(self, normalised)) {
        maybeSetDimmedBackdropPreference(self, id, useDimmedBackdrop);
        return;
      }
      const existingPinned = self.loadingOverlayPinnedMessages.get(id);
      if (existingPinned && normaliseOverlayMessageForCompare(existingPinned) === normalised) {
        maybeSetDimmedBackdropPreference(self, id, useDimmedBackdrop);
        return;
      }
    } else if (unpinnedHeadHasMessage(self, normalised)) {
      maybeSetDimmedBackdropPreference(self, id, useDimmedBackdrop);
      return;
    }
  }

  if (isPinnedSlot) {
    const key = overlayTimestampKey(id, 'pinned');
    const existingTimeout = loadingOverlayTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      loadingOverlayTimeouts.delete(key);
    }
    if (!self.pinnedOverlayIds.includes(id)) {
      self.pinnedOverlayIds.push(id);
      loadingOverlayTimestamps.set(key, Date.now());
    }
    if (message) {
      self.loadingOverlayPinnedMessages.set(id, message);
      // Keep pinned dwell timing tied to the latest pinned copy update.
      loadingOverlayTimestamps.set(key, Date.now());
    }
  }

  if (isUnpinnedSlot) {
    const key = overlayTimestampKey(id, 'unpinned');
    const existingTimeout = loadingOverlayTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      loadingOverlayTimeouts.delete(key);
    }
    if (!self.unpinnedOverlayIds.includes(id)) {
      self.unpinnedOverlayIds.push(id);
      loadingOverlayTimestamps.set(key, Date.now());
    }
    if (normalised.length > 0) {
      if (options?.replace) {
        replaceUnpinnedOverlayMessage(self, id, trimmed);
      } else {
        enqueueUnpinnedOverlayMessage(self, id, trimmed);
      }
    }
  }

  dedupeVisibleOverlayHeads(self);
  maybeSetDimmedBackdropPreference(self, id, useDimmedBackdrop);
}

function removeUnpinnedOverlayImmediate(self: ILoadingOverlayStore, id: string) {
  const key = overlayTimestampKey(id, 'unpinned');
  const existingTimeout = loadingOverlayTimeouts.get(key);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    loadingOverlayTimeouts.delete(key);
  }
  self._removeLoadingOverlaySlot(id, 'unpinned');
}

function applyHideLoadingOverlay(
  self: ILoadingOverlayStore,
  id: string,
  pinned?: boolean,
  options?: HideLoadingOverlayOptions,
) {
  if (DEBUG_LOG) {
    console.log(
      '[Overlay] hideLoadingOverlay',
      id,
      pinned === undefined ? 'both' : pinned ? 'pinned' : 'unpinned',
      options?.immediate ? 'IMMEDIATE' : '',
    );
  }

  const clearPinned = pinned === undefined || pinned === true;
  const clearUnpinned = pinned === undefined || pinned === false;

  if (clearPinned) {
    scheduleOverlayRemoval(self, id, 'pinned');
  }
  if (clearUnpinned) {
    if (options?.immediate) {
      removeUnpinnedOverlayImmediate(self, id);
    } else {
      requestUnpinnedOverlayClose(self, id);
    }
  }
}

function scheduleOverlayRemoval(self: ILoadingOverlayStore, id: string, slot: LoadingOverlaySlot) {
  const key = overlayTimestampKey(id, slot);
  const mapRef = slot === 'pinned' ? self.loadingOverlayPinnedMessages : self.loadingOverlayUnpinnedMessages;
  const idList = slot === 'pinned' ? self.pinnedOverlayIds : self.unpinnedOverlayIds;
  const idPresentInList = idList.includes(id);
  if (!mapRef.has(id) && !idPresentInList) {
    return;
  }
  if (!mapRef.has(id) && idPresentInList) {
    // Defensive cleanup: slot list and map can become temporarily out of sync.
    self._removeLoadingOverlaySlot(id, slot);
    return;
  }

  const existingTimeout = loadingOverlayTimeouts.get(key);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    loadingOverlayTimeouts.delete(key);
  }

  const timestamp = loadingOverlayTimestamps.get(key);
  if (timestamp) {
    const elapsed = Date.now() - timestamp;
    const remainingTime = Math.max(OVERLAY_REMOVAL_DELAY_MS, MIN_LOADING_OVERLAY_DISPLAY_MS - elapsed);
    if (remainingTime <= 0) {
      self._removeLoadingOverlaySlot(id, slot);
      return;
    }

    const timeoutId = setTimeout(() => {
      self._removeLoadingOverlaySlot(id, slot);
    }, remainingTime);

    loadingOverlayTimeouts.set(key, timeoutId);
  } else {
    self._removeLoadingOverlaySlot(id, slot);
  }
}
